import { useQuery } from "@tanstack/react-query";
import { shoppingAccessQuery } from "@/shared/shoppingQueries.ts";
import { currentUserQuery } from "@/hooks/useUser.tsx";
import { queryClient } from "@/lib/query-client.ts";
import { isStepUpGatedPath, stepUpHref } from "@/lib/stepUp.ts";
import { needsStepUp } from "@/shared/stepUp.ts";
import { rememberSavedBrowserOpen } from "@/shared/useSavedBrowserTasks.ts";
import * as Sentry from "@sentry/react";
import { useCallback, useState } from "react";
import { api } from "@/lib/http.ts";
import { cn, resolveApiPath } from "@/lib/utils.ts";
import type { ArtifactAction, HeavyArtifactResponse } from "@/types/artifacts.ts";
import { ChevronDown } from "lucide-react";
import { useIsMobile } from "../../hooks/use-mobile.tsx";
import { Sparkle } from "../icons/customIcons.tsx";
import { Button } from "../ui/button.tsx";
import { GradientDefinition } from "../ui/gradient-def.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu.tsx";
import { errorToast } from "../ui/toast.tsx";
import { flushPendingArtifactEdits } from "./view/pendingArtifactEdits.ts";

const stopBubble = (e: React.MouseEvent | React.KeyboardEvent) => {
  e.stopPropagation();
  e.preventDefault();
};

/**
 * Dispatch the selected artifact action, including POST-backed browser handoffs. Used by
 * this component for in-webview buttons, and by the native-webview bridge in
 * `HeavyArtifactItem` to dispatch taps from the native header.
 *
 * A POST carries a freshly minted `tempClientId`. Deciding a card usually makes
 * the server write a user message onto the thread, and that id is what comes
 * back on the socket frame to say the message belongs to this client — so the
 * feed gives it the active-message treatment (scrolled to the top with the
 * reply streaming in beneath) instead of leaving it wherever it landed. Minted
 * here rather than at the two click handlers so every caller is covered,
 * including the native header's bridge and the omnibox biscuit. Handlers that
 * write no message simply never send it back, and their request schemas leave
 * the field undeclared, where zod drops it.
 */
export const fireArtifactAction = async (action: ArtifactAction): Promise<void> => {
  if (action.method === "GET_EXTERNAL") {
    // A Google or Microsoft connect from an account proven only by a claim link
    // answers OTP_REQUIRED, which a new tab would show as JSON; prove the number
    // first, as the connector gallery does. Read from the cache, so it works from
    // any caller: an unloaded cache reads as "no step-up", which the server
    // refuses anyway.
    const phoneVerifiedBy = queryClient.getQueryData(currentUserQuery().queryKey)?.user
      ?.phoneVerifiedBy;
    if (isStepUpGatedPath(action.handlerPath) && needsStepUp(phoneVerifiedBy)) {
      window.location.assign(stepUpHref(window.location.pathname + window.location.search));
      return;
    }
    // Absolutized because the browser would otherwise resolve the handler path
    // against the web origin, which in production is not the API's. Fully
    // qualified URLs pass through unchanged.
    let href = resolveApiPath(action.handlerPath);
    if (action.id === "take-control" && window.matchMedia("(max-width: 640px)").matches) {
      const url = new URL(href, window.location.origin);
      url.searchParams.set("surface", "ios");
      href = url.toString();
    }
    // With noopener, even a successfully opened tab returns null. That return
    // value cannot distinguish a blocked popup from an isolated sign-in tab.
    window.open(href, "_blank", "noopener,noreferrer");
    return;
  }
  // The server reads the stored artifact to decide what to act on, so anything
  // the card is still holding — a value in a focused field, or a blur-save
  // still in flight — has to land first. Otherwise approving a routine right
  // after editing its "Then do" installs the prompt the user replaced
  // (BIG-6261). Here rather than in the click handler so every caller is
  // covered, including the native header's bridge and the omnibox biscuit.
  await flushPendingArtifactEdits();
  // Random, matching the send path's client ids — it only has to be unique
  // against this client's other in-flight sends, and the server treats it as an
  // opaque value to echo back.
  const savedTaskPath = /^\/api\/a\/threads\/\d+\/browser-tasks\/[^/]+\/(open|resume|cancel)$/.test(
    action.handlerPath,
  );
  await api.postServerPath(action.handlerPath, {
    ...(action.body ?? {}),
    ...(savedTaskPath && window.isWebView ? { surface: "ios" } : {}),
    tempClientId: Math.random(),
  });
  const savedOpen = action.handlerPath.match(
    /^\/api\/a\/threads\/(\d+)\/browser-tasks\/([^/]+)\/open$/,
  );
  if (savedOpen?.[1] && savedOpen[2])
    rememberSavedBrowserOpen(queryClient, Number(savedOpen[1]), savedOpen[2]);
};

type ActionsButtonsProps = {
  /** Ordered actions[]. Index 0 is primary; the rest appear in the split-pill dropdown. */
  actions: ArtifactAction[];
  /**
   * How to render the actions.
   * - `"split-pill"` (default): space-constrained card/footer layout — 1 action
   *   renders as a single pill, 2+ render as a split-pill with a dropdown.
   * - `"flat"`: thread-view layout — all actions render side-by-side as
   *   separate buttons (primary gradient, rest outline). Keeps inline message
   *   artifacts' Accept / Decline / Counter visible without a dropdown.
   * - `"numbered"`: omnibox-overlay biscuit layout — vertically stacked rows
   *   with a small numeric badge. Visual hint that 1/2/3 are keyboard-shortcut
   *   activatable; the numbering is presentational (shortcut handlers aren't
   *   wired up here).
   */
  layout?: "split-pill" | "flat" | "numbered";
  /**
   * Called synchronously as soon as the user clicks — use this to drive
   * optimistic state (e.g. flip the thread to in-progress) before the POST
   * resolves. Fires regardless of whether the POST later succeeds or fails.
   */
  onActionFire?: (action: ArtifactAction) => void;
  /**
   * Called if the POST fails — use this to roll back any optimistic state
   * applied in `onActionFire` so the user isn't stuck with a stale "in
   * progress" badge and missing action buttons.
   */
  onActionError?: (action: ArtifactAction, error: unknown) => void;
  /**
   * Called once the POST lands, when the artifact's real state is committed —
   * use this to refresh whatever else renders it. `onActionFire`'s optimistic
   * patch only reaches the surface that fired; every other one is still holding
   * the artifact as it was before the click.
   */
  onActionSuccess?: (action: ArtifactAction) => void;
  /**
   * Review-queue override: when set, a POST action is **staged** into the batch
   * instead of fired, so a review of three drafts is one request and one agent
   * run (BIG-7346). GET_EXTERNAL still fires immediately — a browser redirect
   * can't be deferred, and the batch endpoint rejects it outright.
   */
  onStage?: (action: ArtifactAction) => void;
  /**
   * Lock the actions out. For surfaces where the card can hold edits that have
   * not been committed yet: an action fires against what the server has stored,
   * so running or approving mid-edit acts on the pre-edit version and silently
   * discards what the user just typed.
   */
  disabled?: boolean;
};

/**
 * Renders an artifact's declarative `actions[]`:
 *   0 → nothing (footer shows "See thread" only)
 *   1 → single primary pill
 *   2+ → split pill (primary + circular chevron dropdown listing all actions)
 *
 * Agnostic of artifact type — the server decides labels, variants and handler
 * paths. Matches the split-pill pattern prototyped in `frank/homeCard`.
 */
export const ArtifactActionsButtons = (props: ActionsButtonsProps) =>
  props.actions.some((action) => action.requiresShoppingPayments) ? (
    <ShoppingActionsButtons {...props} />
  ) : (
    <ActionsButtons {...props} />
  );
const ShoppingActionsButtons = (props: ActionsButtonsProps) => {
  const access = useQuery(shoppingAccessQuery());
  return (
    <ActionsButtons
      {...props}
      actions={props.actions.filter(
        (action) =>
          !action.requiresShoppingPayments ||
          (access.data?.enabled === true && access.data.paymentsEnabled === true),
      )}
    />
  );
};
const ActionsButtons = ({
  actions,
  layout = "split-pill",
  onActionFire,
  onActionError,
  onActionSuccess,
  onStage,
  disabled = false,
}: ActionsButtonsProps) => {
  // Stays true after a successful fire, unlike `isFiring`. Surfaces that keep
  // the artifact on screen (the canvas card mid-review) hold a live Send between
  // the POST resolving and the refetch that strips `actions` — long enough to
  // send the same draft twice.
  const [hasFired, setHasFired] = useState(false);
  const [isFiring, setIsFiring] = useState(false);
  const isMobile = useIsMobile();

  const isLocked = (action: ArtifactAction) =>
    isFiring || disabled || (hasFired && !action.reusable);

  const handleFire = useCallback(
    async (action: ArtifactAction) => {
      if (disabled) return;
      // Queue mode: stage the (POST) action rather than firing it now. The stage
      // handler moves the review on, which swaps what this surface is showing —
      // so none of the fired-state below applies, and the optimistic
      // "in progress" flip would be a lie about a thread that isn't running yet.
      if (onStage && action.method === "POST" && !action.reusable) {
        onStage(action);
        return;
      }
      if (isFiring) return;
      setIsFiring(true);
      // Fire the optimistic update IMMEDIATELY so the UI responds before the
      // POST resolves. The artifact's `actions` array gets cleared by the
      // caller's optimistic update, so the button disappears on the next
      // render — the `isFiring` guard is a belt-and-suspenders lockout for the
      // brief window before that paint lands.
      //
      // A browser handoff has not completed sign-in, so callers like
      // ThreadCardFooter must keep the pending card available.
      if (action.method !== "GET_EXTERNAL" && !action.reusable) {
        onActionFire?.(action);
      }
      try {
        await fireArtifactAction(action);
        // A redirect decides nothing — the artifact is still waiting when the
        // user comes back, so its buttons have to stay live, and there is no
        // committed state for anyone to refresh to.
        if (action.method !== "GET_EXTERNAL" && !action.reusable) {
          setHasFired(true);
          onActionSuccess?.(action);
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error("Artifact action failed:", error);
        errorToast(error instanceof Error ? error.message : "Action failed. Please try again.");
        onActionError?.(action, error);
      } finally {
        setIsFiring(false);
      }
    },
    [isFiring, onActionFire, onActionError, onActionSuccess, onStage, disabled],
  );

  if (actions.length === 0) return null;

  // Numbered layout — vertically stacked rows, each labeled with a small
  // index badge. Used by the omnibox-overlay biscuit. Renders BEFORE the
  // split-vs-single branching below because the structure is uniform across
  // 1, 2, 3+ actions.
  if (layout === "numbered") {
    return (
      <div className="flex flex-col">
        {actions.map((action, i) => (
          <Button
            key={action.id}
            variant="ghost"
            disabled={isLocked(action)}
            onClick={(e) => {
              stopBubble(e);
              void handleFire(action);
            }}
            className={cn(
              "justify-start h-auto py-2 px-2 -mx-2 text-sm font-normal",
              action.variant === "destructive" && "text-destructive",
            )}
            data-statsig-name={action.statsigName ?? `artifact-action-${action.id}`}
          >
            <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-muted text-muted-foreground text-xs font-medium mr-2 shrink-0">
              {i + 1}
            </span>
            <span>{action.label}</span>
          </Button>
        ))}
      </div>
    );
  }

  const [primary, ...rest] = actions;

  // Outer wrapper rules:
  // - "flat" (thread view): wrap to multiple rows when buttons don't fit; no
  //   shrinking pressure from siblings.
  // - "split-pill" (card footer): hug content so Primary + "See thread" stay
  //   tightly grouped on the left; Archive's `ml-auto` keeps it pinned right.
  //   `min-w-0` (without `flex-1`) lets the pill shrink past its natural
  //   content width when a long label would otherwise push siblings off-card,
  //   so `truncate` on the inner label can produce the ellipsis.
  const outerWrapperClass =
    layout === "flat" ? "flex flex-row flex-wrap gap-2" : "flex flex-row gap-2 min-w-0";

  if (rest.length === 0) {
    // A lone action is usually the card's CTA (Save, Approve, Open) and wears the
    // gradient pill. But some cards offer only a way OUT — a kickoff mid-send, an
    // invitation with no times to send — and dressing "Discard" as the CTA invites
    // the tap it should be discouraging. Those render as a plain outline button,
    // and `mr-auto` pulls them off the footer's right-hand CTA slot to sit beside
    // the status date instead.
    const isSoleEscape = primary.variant !== "primary";
    return (
      <div className={cn(outerWrapperClass, isSoleEscape && "mr-auto")}>
        <GradientDefinition />
        <Button
          variant={isSoleEscape ? "outline" : "gradientOutline"}
          className={cn("group gap-1.5", layout !== "flat" && "min-w-0 max-w-full")}
          disabled={isLocked(primary)}
          data-statsig-name={primary.statsigName ?? "artifact-action-primary"}
          onClick={(e) => {
            stopBubble(e);
            void handleFire(primary);
          }}
        >
          {!isSoleEscape && (
            <Sparkle className="w-4 h-4 shrink-0 gradient-brand-purple-stroke group-hover:stroke-white" />
          )}
          <span className={cn(layout !== "flat" && "truncate min-w-0")}>{primary.label}</span>
        </Button>
      </div>
    );
  }

  // 2+ actions, flat layout — thread view. Side-by-side buttons, no dropdown.
  if (layout === "flat") {
    // Discard renders to the LEFT of the primary, so the committing button sits
    // furthest from the card's other content and never occupies the slot the eye
    // lands on first. Only `cancel` is hoisted, and only ahead of the primary —
    // a three-way flow (Accept / Decline / Counter) keeps the order the server
    // authored, where each button is a real answer rather than a way out.
    const ordered = [
      ...rest.filter((a) => a.id === "cancel"),
      primary,
      ...rest.filter((a) => a.id !== "cancel"),
    ];
    return (
      <div className={outerWrapperClass}>
        <GradientDefinition />
        {ordered.map((action) =>
          action === primary ? (
            <Button
              key={action.id}
              variant="gradientOutline"
              className="group gap-1.5"
              disabled={isLocked(action)}
              data-statsig-name={action.statsigName ?? "artifact-action-primary"}
              onClick={(e) => {
                stopBubble(e);
                void handleFire(action);
              }}
            >
              <Sparkle className="w-4 h-4 gradient-brand-purple-stroke group-hover:stroke-white" />
              {action.label}
            </Button>
          ) : (
            <Button
              key={action.id}
              variant="outline"
              disabled={isLocked(action)}
              data-statsig-name={action.statsigName ?? `artifact-action-${action.id}`}
              onClick={(e) => {
                stopBubble(e);
                void handleFire(action);
              }}
            >
              {action.label}
            </Button>
          ),
        )}
      </div>
    );
  }

  // 2+ actions, split-pill layout — home card footer.
  //
  // The trigger structure differs by viewport:
  // - Desktop (≥768px): traditional split-pill — clicking the label fires the
  //   primary action; clicking the chevron sub-circle opens the dropdown.
  // - Mobile (<768px): the entire pill opens the dropdown. No default-action
  //   submit on tap — touch targets are imprecise enough that an accidental
  //   primary-action submission is a worse failure mode than an extra tap.
  //   The chevron sits flush at the right edge of the unified pill.

  // Shared visual shell — same look on both variants.
  const pillShellClass =
    "group/btn flex items-center h-10 min-w-0 max-w-full rounded-full border border-primary text-sm font-medium cursor-pointer transition-all duration-300 bg-gradient-to-br from-brand-500 to-[#c084fc] bg-clip-text text-transparent hover:from-[#5f79f5] hover:to-[#c084fc] hover:text-white hover:border-transparent hover:bg-clip-border";

  const dropdownContent = (
    <DropdownMenuContent
      align="start"
      side="bottom"
      sideOffset={4}
      // Cap dropdown width to the card's content budget so verbose labels
      // wrap to multi-line rather than escaping the card.
      className="max-w-[280px]"
      onClick={(e) => e.stopPropagation()}
    >
      {actions.map((action) => (
        <DropdownMenuItem
          key={action.id}
          disabled={isLocked(action)}
          className="whitespace-normal break-words"
          data-statsig-name={action.statsigName ?? `artifact-action-${action.id}`}
          onClick={(e) => {
            stopBubble(e);
            void handleFire(action);
          }}
        >
          {action.verboseLabel ?? action.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  );

  return (
    <div className={outerWrapperClass}>
      <GradientDefinition />
      <DropdownMenu modal>
        <DropdownMenuTrigger asChild>
          {isMobile ? (
            <button
              type="button"
              className={cn(pillShellClass, "gap-2 px-4 py-2")}
              disabled={actions.every(isLocked)}
              data-statsig-name="artifact-action-mobile-dropdown"
              onClick={stopBubble}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <span className="truncate min-w-0 text-left">{primary.label}</span>
              <ChevronDown className="size-3.5 shrink-0 text-primary group-hover/btn:text-white" />
            </button>
          ) : (
            <div className={cn(pillShellClass, "gap-1 px-4 pr-2 py-2")} onClick={stopBubble}>
              <button
                type="button"
                className="truncate min-w-0 bg-transparent border-0 p-0 cursor-pointer text-inherit"
                disabled={isLocked(primary)}
                data-statsig-name={primary.statsigName ?? "artifact-action-primary"}
                onClick={(e) => {
                  stopBubble(e);
                  void handleFire(primary);
                }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {primary.label}
              </button>
              <div className="shrink-0 inline-flex items-center justify-center size-6 rounded-full cursor-pointer bg-primary/10 hover:bg-primary/20 group-hover/btn:bg-white/20 group-hover/btn:hover:bg-black/15 transition-colors">
                <ChevronDown className="size-3.5 text-primary group-hover/btn:text-white" />
              </div>
            </div>
          )}
        </DropdownMenuTrigger>
        {dropdownContent}
      </DropdownMenu>
    </div>
  );
};

/** Pull ordered actions off the first (active) artifact on a thread, if any. */
export const getArtifactActions = (
  artifact: HeavyArtifactResponse | undefined,
): ArtifactAction[] | undefined => {
  if (!artifact) return undefined;
  if (!("actions" in artifact) || !artifact.actions) return undefined;
  return artifact.actions;
};
